import { keepPreviousData } from '@tanstack/react-query'
import { Surface } from '@repo/rpc-types-creativefabrica-common-v1/surface/v1'
import type { UserQuery } from '@repo/cf-user/gen/user'
import { useUserQuery } from 'auth/hooks'
import ms from 'ms'

const queryOptions = {
  placeholderData: keepPreviousData,
  staleTime: ms('15s'),
}

type SubscriptionAccess = NonNullable<NonNullable<UserQuery['me']>['user']>['subscriptionAccess']
type StudioPlan = keyof Pick<SubscriptionAccess, 'allAccess' | 'studioAi' | 'studioUnlimited'>
type StudioPlanAccess = SubscriptionAccess[StudioPlan]

const STUDIO_PLANS: readonly StudioPlan[] = ['allAccess', 'studioAi']
const UNLIMITED_PLANS: readonly StudioPlan[] = ['studioUnlimited']

/** Each surface sells its own plans, so a subscription only counts on the surface it was bought for. */
function getSurfacePlans(surface: Surface): readonly StudioPlan[] {
  return surface === Surface.UNLIMITED ? UNLIMITED_PLANS : STUDIO_PLANS
}

function isActivePaidSubscription(access: StudioPlanAccess): boolean {
  return access.isSubscribed && access.subscription?.isTrial !== true
}

function isActiveTrialSubscription(access: StudioPlanAccess): boolean {
  return access.isSubscribed && access.subscription?.isTrial === true
}

function hasSurfaceSubscription(
  data: UserQuery,
  surface: Surface,
  matchesSubscription: (access: StudioPlanAccess) => boolean,
): boolean {
  const access = data.me?.user?.subscriptionAccess
  if (!access) {
    return false
  }
  return getSurfacePlans(surface).some(plan => matchesSubscription(access[plan]))
}

/**
 * Returns true when the current user pays for a plan of `surface`, outside its trial: All Access or
 * Studio AI on Studio and Freemode, Studio Unlimited on Unlimited.
 *
 * Used to gate coin tipping — trial and non-subscribed users cannot tip.
 */
export function useIsPaidSubscriberQuery(surface: Surface) {
  return useUserQuery(undefined, {
    ...queryOptions,
    select: data => hasSurfaceSubscription(data, surface, isActivePaidSubscription),
  })
}

/**
 * Returns true when the current user is trialling a plan of `surface`. Used to show "Upgrade" vs
 * "Subscribe" copy.
 */
export function useIsOnAnyTrialQuery(surface: Surface) {
  return useUserQuery(undefined, {
    ...queryOptions,
    select: data => hasSurfaceSubscription(data, surface, isActiveTrialSubscription),
  })
}
